import React, { useState, useEffect, useMemo } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Space,
  Table,
  Tag,
  Typography,
  Progress,
  Segmented,
  Button,
  theme
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartLine,
  faFlask,
  faCheckCircle,
  faHourglassHalf,
  faExclamationTriangle,
  faArrowUp,
  faArrowDown
} from '@fortawesome/free-solid-svg-icons'
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { ORDER_STATUS, PRIORITY_LEVEL_VALUES } from '../utils/constants'
import { useLabOrders } from '../hooks/useLabOrders'
import { getTests } from '../utils/utils'
import { hexToRGBA } from '../../lib/utils'

// Extend dayjs with plugins
dayjs.extend(isBetween)

const { RangePicker } = DatePicker
const { Title, Text } = Typography

const Analytics = () => {
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()])
  const [viewType, setViewType] = useState('overview')
  const [allTests, setAllTests] = useState([])
  const [loadingTestData, setLoadingTestData] = useState(false)
  const { orders, fetchOrders } = useLabOrders()
  const { token } = theme.useToken()

  const analyticsTabOptions = [
    { key: 'overview', label: 'Overview' },
    { key: 'performance', label: 'Performance' },
    { key: 'tests', label: 'Tests' }
  ]

  console.log(orders)

  useEffect(() => {
    fetchOrders()
    // eslint-disable-next-line
  }, [])

  // Load all tests when switching to tests tab
  useEffect(() => {
    if (viewType === 'tests' && allTests.length === 0) {
      loadAllTests()
    }
  }, [viewType])

  const loadAllTests = async () => {
    try {
      setLoadingTestData(true)
      // Load a large number of tests to get comprehensive data
      const tests = await getTests({}, 1, 10000)
      setAllTests(tests)
    } catch (error) {
      console.error('Failed to load tests:', error)
    } finally {
      setLoadingTestData(false)
    }
  }

  // Handle date range changes
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      setDateRange(dates)
    } else {
      // If cleared, default to last 30 days
      setDateRange([dayjs().subtract(30, 'days'), dayjs()])
    }
  }

  // Filter orders by date range
  const filteredOrders = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return orders
    }

    return orders.filter((order) => {
      if (!order.createdAt) return false

      const orderDate = dayjs(order.createdAt)
      const startDate = dayjs(dateRange[0]).startOf('day')
      const endDate = dayjs(dateRange[1]).endOf('day')

      return orderDate.isBetween(startDate, endDate, null, '[]')
    })
  }, [orders, dateRange])

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalOrders = filteredOrders.length
    const completedOrders = filteredOrders.filter(
      (o) => o.timeline && o.timeline.some((t) => t.status === ORDER_STATUS.COMPLETE)
    ).length
    const pendingOrders = filteredOrders.filter(
      (o) => o.timeline && o.timeline.some((t) => t.status === ORDER_STATUS.ORDERED)
    ).length
    const cancelledOrders = filteredOrders.filter(
      (o) => o.timeline && o.timeline.some((t) => t.status === ORDER_STATUS.EXPIRED)
    ).length
    const urgentOrders = filteredOrders.filter((o) => o.orderData.priority === PRIORITY_LEVEL_VALUES.STAT).length
    const completionRate = totalOrders > 0 ? ((completedOrders / totalOrders) * 100).toFixed(1) : 0
    const cancellationRate = totalOrders > 0 ? ((cancelledOrders / totalOrders) * 100).toFixed(1) : 0

    return {
      totalOrders,
      completedOrders,
      pendingOrders,
      cancelledOrders,
      urgentOrders,
      completionRate,
      cancellationRate
    }
  }, [filteredOrders])

  const { totalOrders, completedOrders, pendingOrders, urgentOrders, completionRate, cancellationRate } = statistics

  // Calculate average turnaround time (mock data)
  const averageTAT = 18.5 // hours

  // Prepare data for charts
  const ordersByStatus = useMemo(() => {
    return Object.values(ORDER_STATUS).map((status) => ({
      status,
      count: filteredOrders.filter((o) => o.timeline && o.timeline.some((t) => t.status === status)).length
    }))
  }, [filteredOrders])

  const ordersByPriority = useMemo(() => {
    return Object.values(PRIORITY_LEVEL_VALUES).map((priority) => ({
      priority,
      count: filteredOrders.filter((o) => o.orderData.priority === priority).length
    }))
  }, [filteredOrders])

  // Daily order trends - respects the selected date range
  const dailyOrders = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return []
    }

    const data = []
    const startDate = dayjs(dateRange[0])
    const endDate = dayjs(dateRange[1])
    const daysDiff = endDate.diff(startDate, 'days')

    // Limit to reasonable number of data points for chart readability
    const maxDataPoints = 30
    const stepSize = Math.max(1, Math.ceil(daysDiff / maxDataPoints))

    for (let i = 0; i <= daysDiff; i += stepSize) {
      const currentDate = dayjs(startDate).add(i, 'days')
      const nextDate = dayjs(currentDate).add(stepSize, 'days')

      let count = 0
      if (stepSize === 1) {
        // Daily granularity
        count = filteredOrders.filter(
          (o) => dayjs(o.createdAt).format('YYYY-MM-DD') === currentDate.format('YYYY-MM-DD')
        ).length
      } else {
        // Weekly/monthly granularity for larger date ranges
        count = filteredOrders.filter((o) => {
          const orderDate = dayjs(o.createdAt)
          return orderDate.isBetween(currentDate, nextDate, 'day', '[)')
        }).length
      }

      data.push({
        date: stepSize === 1 ? currentDate.format('MMM DD') : currentDate.format('MMM DD'),
        count
      })
    }

    return data
  }, [filteredOrders, dateRange])

  // Most ordered tests with real test names
  const topTests = useMemo(() => {
    const testFrequency = {}

    // Count frequency of test codes
    filteredOrders.forEach((order) => {
      if (order.orderData && order.orderData.orderedTests) {
        order.orderData.orderedTests.forEach((testCode) => {
          testFrequency[testCode] = (testFrequency[testCode] || 0) + 1
        })
      }
    })

    // Map test codes to test names and get top 10
    return Object.entries(testFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([testCode, count]) => {
        const test = allTests.find((t) => t.code === testCode)
        return {
          test: test ? test.name : testCode,
          testCode,
          count
        }
      })
  }, [filteredOrders, allTests])

  // Test categories distribution with real data
  const testCategoriesDistribution = useMemo(() => {
    const categoryFrequency = {}

    filteredOrders.forEach((order) => {
      if (order.orderData && order.orderData.orderedTests) {
        order.orderData.orderedTests.forEach((testCode) => {
          const test = allTests.find((t) => t.code === testCode)
          if (test && test.category) {
            categoryFrequency[test.category] = (categoryFrequency[test.category] || 0) + 1
          }
        })
      }
    })

    return Object.entries(categoryFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([category, count]) => ({ category, count }))
  }, [filteredOrders, allTests])

  // Colors for charts
  const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c']

  // Custom label for pie chart
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, status, count }) => {
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text x={x} y={y} fill='white' textAnchor={x > cx ? 'start' : 'end'} dominantBaseline='central'>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <p>{`${label}: ${payload[0].value}`}</p>
        </div>
      )
    }
    return null
  }

  const performanceColumns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric'
    },
    {
      title: 'Current Period',
      dataIndex: 'current',
      key: 'current',
      render: (value) => <strong>{value}</strong>
    },
    {
      title: 'Previous Period',
      dataIndex: 'previous',
      key: 'previous'
    },
    {
      title: 'Change',
      dataIndex: 'change',
      key: 'change',
      render: (value) => {
        const isPositive = value > 0
        return (
          <Space>
            <FontAwesomeIcon
              icon={isPositive ? faArrowUp : faArrowDown}
              style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}
            />
            <Text type={isPositive ? 'success' : 'danger'}>{Math.abs(value)}%</Text>
          </Space>
        )
      }
    }
  ]

  const performanceData = [
    {
      key: '1',
      metric: 'Total Orders',
      current: totalOrders,
      previous: Math.floor(totalOrders * 0.85),
      change: 15
    },
    {
      key: '2',
      metric: 'Completion Rate',
      current: `${completionRate}%`,
      previous: '88.5%',
      change: 3.5
    },
    {
      key: '3',
      metric: 'Cancellation Rate',
      current: `${cancellationRate}%`,
      previous: '5.2%',
      change: -2.2
    }
  ]

  return (
    <Card style={{ height: '100%', overflow: 'auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}></Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format='YYYY-MM-DD'
              allowClear={true}
              placeholder={['Start Date', 'End Date']}
            />
            <div
              style={{
                display: 'flex',
                gap: '8px',
                backgroundColor: '#ffffff',
                padding: '8px',
                borderRadius: '12px',
                border: '1px solid #e6f4ff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
              }}
            >
              {analyticsTabOptions.map((tab) => (
                <Button
                  key={tab.key}
                  type={viewType === tab.key ? 'primary' : 'text'}
                  size='medium'
                  onClick={() => setViewType(tab.key)}
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    border: viewType === tab.key ? 'none' : '1px solid #d9d9d9',
                    fontWeight: viewType === tab.key ? '600' : '500',
                    backgroundColor: viewType === tab.key ? '#1890ff' : '#ffffff',
                    color: viewType === tab.key ? '#ffffff' : '#262626',
                    boxShadow:
                      viewType === tab.key ? '0 2px 4px rgba(24, 144, 255, 0.3)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease',
                    minWidth: '100px',
                    margin: '0 2px'
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
          </Space>
        </Col>
      </Row>

      {viewType === 'overview' && (
        <>
          {/* Statistics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title='Total Orders'
                  value={totalOrders}
                  prefix={<FontAwesomeIcon icon={faFlask} />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title='Completed'
                  value={completedOrders}
                  prefix={<FontAwesomeIcon icon={faCheckCircle} />}
                  valueStyle={{ color: '#52c41a' }}
                  suffix={
                    <Text type='secondary' style={{ fontSize: 14 }}>
                      ({completionRate}%)
                    </Text>
                  }
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title='Pending'
                  value={pendingOrders}
                  prefix={<FontAwesomeIcon icon={faHourglassHalf} />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title='Urgent Orders'
                  value={urgentOrders}
                  prefix={<FontAwesomeIcon icon={faExclamationTriangle} />}
                  valueStyle={{ color: urgentOrders > 0 ? '#ff4d4f' : '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title='Order Trends (Last 30 Days)'>
                <ResponsiveContainer width='100%' height={300}>
                  <LineChart data={dailyOrders}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='date' />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type='monotone' dataKey='count' stroke='#1890ff' strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title='Orders by Status'>
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie
                      data={ordersByStatus}
                      cx='50%'
                      cy='50%'
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={80}
                      fill='#8884d8'
                      dataKey='count'
                    >
                      {ordersByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [value, props.payload.status]}
                      labelFormatter={() => ''}
                    />
                    <Legend formatter={(value, entry) => entry.payload.status} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {viewType === 'performance' && (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title='Performance Metrics'>
                <Table columns={performanceColumns} dataSource={performanceData} pagination={false} />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card title='Orders by Priority'>
                <Row gutter={16}>
                  {ordersByPriority.map((item, index) => (
                    <Col span={12} key={index} style={{ marginBottom: 16 }}>
                      <div>
                        <Text>{item.priority === PRIORITY_LEVEL_VALUES.STAT ? 'Stat' : 'Routine'}</Text>
                        <Progress
                          percent={((item.count / totalOrders) * 100).toFixed(1)}
                          strokeColor={
                            item.priority === PRIORITY_LEVEL_VALUES.STAT
                              ? '#ff4d4f'
                              : item.priority === PRIORITY_LEVEL_VALUES.ROUTINE
                              ? '#faad14'
                              : '#1890ff'
                          }
                        />
                        <Text type='secondary'>{item.count} orders</Text>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {viewType === 'tests' && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card title='Most Ordered Tests' loading={loadingTestData}>
                {topTests.length > 0 ? (
                  <ResponsiveContainer width='100%' height={600}>
                    <BarChart
                      data={topTests.map((item, index) => ({
                        name: item.test.length > 20 ? `${item.test.substring(0, 20)}...` : item.test,
                        fullName: item.test,
                        count: item.count,
                        testCode: item.testCode,
                        index: index
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis
                        dataKey='name'
                        angle={-45}
                        textAnchor='end'
                        height={100}
                        interval={0}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis
                        label={{ value: 'Number of Orders', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                        domain={[0, 'dataMax + 1']}
                      />
                      <Tooltip
                        formatter={(value) => [`${value}`, 'Orders']}
                        labelFormatter={(label) => {
                          if (typeof label === 'string') {
                            const item = topTests.find(
                              (t) => t.test.startsWith(label.replace('...', '')) || t.test === label
                            )
                            return item ? item.test : label
                          }
                          return label
                        }}
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}
                      />
                      <Bar dataKey='count' fill='#1890ff' radius={[4, 4, 0, 0]}>
                        {topTests.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type='secondary'>No test data available for the selected period</Text>
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title='Test Categories Distribution' loading={loadingTestData}>
                <Space direction='vertical' style={{ width: '100%' }}>
                  {testCategoriesDistribution.length > 0 ? (
                    testCategoriesDistribution.map((item) => {
                      const maxCount = Math.max(...testCategoriesDistribution.map((d) => d.count))
                      const percentage = maxCount > 0 ? ((item.count / maxCount) * 100).toFixed(0) : 0
                      return (
                        <div key={item.category} style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>{item.category}</Text>
                            <Text strong>{item.count}</Text>
                          </div>
                          <Progress percent={percentage} showInfo={false} size='small' />
                        </div>
                      )
                    })
                  ) : (
                    <Text type='secondary'>No test category data available</Text>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card title='Test Volume Trends' loading={loadingTestData}>
                <Table
                  columns={[
                    { title: 'Test Name', dataIndex: 'test', key: 'test' },
                    { title: 'Current Period', dataIndex: 'current', key: 'current' },
                    { title: 'Previous Period', dataIndex: 'previous', key: 'previous' },
                    {
                      title: 'Trend',
                      dataIndex: 'trend',
                      key: 'trend',
                      render: (trend) => (
                        <Tag color={trend > 0 ? 'green' : trend < 0 ? 'red' : 'default'}>
                          {trend > 0 ? '+' : ''}
                          {trend}%
                        </Tag>
                      )
                    }
                  ]}
                  dataSource={topTests.map((testItem, index) => {
                    // Calculate current period (based on date range)
                    const currentPeriod = testItem.count

                    // Calculate previous period (same duration before the current range)
                    const currentDuration = dateRange[1].diff(dateRange[0], 'days')
                    const previousStart = dateRange[0].subtract(currentDuration, 'days')
                    const previousEnd = dateRange[0]

                    const previousPeriodOrders = orders.filter((order) => {
                      const orderDate = dayjs(order.createdAt)
                      return orderDate.isBetween(previousStart, previousEnd, null, '[]')
                    })

                    const previousCount = previousPeriodOrders.filter(
                      (order) =>
                        order.orderData &&
                        order.orderData.orderedTests &&
                        order.orderData.orderedTests.includes(testItem.testCode)
                    ).length

                    const trend =
                      previousCount > 0
                        ? Math.round(((currentPeriod - previousCount) / previousCount) * 100)
                        : currentPeriod > 0
                        ? 100
                        : 0

                    return {
                      key: index,
                      test: testItem.test,
                      current: currentPeriod,
                      previous: previousCount,
                      trend: trend
                    }
                  })}
                  pagination={false}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Card>
  )
}

export default Analytics
